/**
 * Seletor de catálogo dentro da conversa — criado 02/09/2026 (Sabe).
 * Mostra só o que está disponível no ciclo. O arquivo já foi preparado no cadastro,
 * então enviar aqui não reprocessa nada.
 * Funciona igual nos dois canais: dentro de um atendimento, a API oficial também
 * aceita anexo livre (a janela de conversa está aberta).
 */
import React, { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { toast } from "react-toastify";

import toastError from "../../errors/toastError";
import api from "../../services/api";

const emMB = (b) => (!b ? "" : `${(b / 1048576).toFixed(1)} MB`);

const CatalogoSelectModal = ({ open, onClose, ticketId }) => {
  const [catalogos, setCatalogos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [mensagem, setMensagem] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/catalogos");
      setCatalogos(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelecionado(null);
      setMensagem("");
      carregar();
    }
  }, [open, carregar]);

  const enviar = async (catalogo) => {
    if (!catalogo || enviando) return;
    setSelecionado(catalogo);
    setEnviando(true);
    try {
      const { data } = await api.post(`/catalogos/${catalogo.id}/enviar`, {
        ticketId,
        body: mensagem,
      });
      toast.success(data?.encaminhado ? "Catálogo encaminhado." : "Catálogo enviado.");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !enviando && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Enviar catálogo</DialogTitle>
      <DialogContent dividers>
        {carregando && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!carregando && catalogos.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
            Nenhum catálogo disponível no momento. Um administrador precisa cadastrar o
            material do ciclo em Catálogos.
          </Typography>
        )}

        {!carregando && catalogos.length > 0 && (
          <>
            <TextField
              label="Mensagem junto (opcional)"
              placeholder="Escreva algo para ir com o catálogo"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 2 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Clique no catálogo para enviar
            </Typography>
            <List dense sx={{ maxHeight: 300, overflowY: "auto", p: 0 }}>
              {catalogos.map((c) => (
                <ListItemButton
                  key={c.id}
                  onClick={() => enviar(c)}
                  disabled={enviando}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <ListItemText
                    primary={c.nome}
                    secondary={[c.descricao, emMB(c.tamanho)].filter(Boolean).join(" · ")}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    disabled={enviando}
                    sx={{ ml: 2, flexShrink: 0, pointerEvents: "none" }}
                  >
                    {enviando && selecionado?.id === c.id ? "Enviando…" : "Enviar"}
                  </Button>
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogoSelectModal;
